// 入室 / 再接続時の「既存の絵の再構築」(snapshot 読み込み + deltaEvents 再生) の診断。
//
// 背景 (2026-10-24 本番 feedback「既存の絵が入り直すと読み込まれない」): 描画の多い部屋では
// room.welcome.deltaEvents (直近 Undo horizon = 10 分ぶん) が約 1 万件になり、その入室で
// 失敗していた。サーバー側にエラーは無く、クライアントの失敗は console.warn のみで
// Sentry に届かなかったため確定できない。ここは観測だけを足す (描画結果・制御フローは変えない)。
//
// PII (uid / 表示名 / roomId / イベント中身) は extra に入れない。計測は performance.now() のみ。

import { captureWarning } from '../sentryCapture.js';

export type JoinReplaySource = 'welcome' | 'resume' | 'restore';
export type JoinReplayStage = 'applySnapshot' | 'replay' | 'ingestUndo';

/**
 * 「重い再生」とみなす deltaEvents 件数。1,400 件の入室は成功し 1 万件で失敗した事実から、
 * その中間の 5,000 件を目安にした (閾値は暫定。Sentry の分布を見て調整する)。
 */
export const HEAVY_REPLAY_EVENT_COUNT = 5000;
/** 「重い再生」とみなす replay + ingestUndo の合計 ms。1 秒超は描画ロック (hydrating) が体感で長い。 */
export const HEAVY_REPLAY_MS = 1000;

const MAX_MESSAGE_LENGTH = 200;

export type Report = (message: string, extra: Record<string, unknown>) => void;

export const describeError = (err: unknown): { errorName: string; errorMessage: string } => {
  if (err instanceof Error) {
    return { errorName: err.name, errorMessage: err.message.slice(0, MAX_MESSAGE_LENGTH) };
  }
  return { errorName: typeof err, errorMessage: String(err).slice(0, MAX_MESSAGE_LENGTH) };
};

const readDeviceMemory = (): number | undefined => {
  try {
    const m = (globalThis as { navigator?: { deviceMemory?: unknown } }).navigator?.deviceMemory;
    return typeof m === 'number' ? m : undefined;
  } catch {
    return undefined;
  }
};

const defaultNow = (): number => performance.now();

// --- 1. snapshot 読み込みの失敗 -------------------------------------------------

export interface SnapshotOutcomeLike {
  readonly outcome: string;
  readonly cause?: string;
}

/** ok でなく、かつ aborted (正常な中断) でないとき報告する。 */
export const shouldReportSnapshotLoadFailure = (out: SnapshotOutcomeLike): boolean =>
  out.outcome !== 'ok' && out.cause !== 'aborted';

export const reportSnapshotLoadFailure = (
  out: SnapshotOutcomeLike,
  source: JoinReplaySource,
  report: Report = captureWarning,
): void => {
  try {
    if (!shouldReportSnapshotLoadFailure(out)) return;
    report('snapshot load failed', {
      source,
      outcome: out.outcome,
      cause: out.cause ?? null,
    });
  } catch {
    // best-effort
  }
};

// 再生 probe が既に報告した例外 (外側の catch での二重報告を避ける)。
const reportedErrors = new WeakSet<object>();
export const isAlreadyReported = (err: unknown): boolean =>
  typeof err === 'object' && err !== null && reportedErrors.has(err);

export const reportSnapshotLoadThrew = (
  err: unknown,
  source: JoinReplaySource,
  report: Report = captureWarning,
): void => {
  try {
    if (isAlreadyReported(err)) return;
    report('snapshot load threw', { source, ...describeError(err) });
  } catch {
    // best-effort
  }
};

// --- 2/3. 再生 / apply の例外と重い再生 -----------------------------------------

export const shouldReportHeavyReplay = (eventCount: number, replayAndIngestMs: number): boolean =>
  eventCount >= HEAVY_REPLAY_EVENT_COUNT || replayAndIngestMs >= HEAVY_REPLAY_MS;

export interface ReplayProbe {
  /** 段階を計測して実行する。throw したら 1 件報告し、例外は再 throw する。 */
  run<T>(stage: JoinReplayStage, fn: () => T): T;
  /** 正常終了時に呼ぶ。重ければ 1 件報告する。 */
  finish(): void;
}

export const createReplayProbe = (
  source: JoinReplaySource,
  eventCount: number,
  report: Report = captureWarning,
  now: () => number = defaultNow,
): ReplayProbe => {
  const startedAt = now();
  const ms: Record<JoinReplayStage, number> = { applySnapshot: 0, replay: 0, ingestUndo: 0 };
  return {
    run<T>(stage: JoinReplayStage, fn: () => T): T {
      const t0 = now();
      try {
        const r = fn();
        ms[stage] += now() - t0;
        return r;
      } catch (err) {
        try {
          if (typeof err === 'object' && err !== null) reportedErrors.add(err);
          report('join replay failed', {
            source,
            stage,
            eventCount,
            elapsedMs: Math.round(now() - startedAt),
            ...describeError(err),
          });
        } catch {
          // best-effort
        }
        throw err;
      }
    },
    finish(): void {
      try {
        const replayMs = Math.round(ms.replay);
        const ingestUndoMs = Math.round(ms.ingestUndo);
        if (!shouldReportHeavyReplay(eventCount, replayMs + ingestUndoMs)) return;
        const deviceMemory = readDeviceMemory();
        report('join replay heavy', {
          source,
          eventCount,
          replayMs,
          ingestUndoMs,
          applySnapshotMs: Math.round(ms.applySnapshot),
          ...(deviceMemory === undefined ? {} : { deviceMemory }),
        });
      } catch {
        // best-effort
      }
    },
  };
};
